import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { IconCheck, IconChevronDown } from "@/components/icons";
import { useAnchoredOverlay } from "@/hooks/useAnchoredOverlay";
import { useClickOutside } from "@/hooks/useClickOutside";

export type AdminMenuSelectOption<T extends string> = {
  value: T;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
  hint?: string;
  description?: string;
  group?: string;
};

function optionGroups<T extends string>(options: Array<AdminMenuSelectOption<T>>) {
  const groups: Array<{ label: string | null; items: Array<AdminMenuSelectOption<T>> }> =
    [];
  for (const option of options) {
    const label = option.group?.trim() || null;
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.items.push(option);
    } else {
      groups.push({ label, items: [option] });
    }
  }
  return groups;
}

export function AdminMenuSelect<T extends string>({
  value,
  options,
  onChange,
  placeholder = "Выберите",
  ariaLabel,
  emptyLabel,
  variant = "field",
}: {
  value: T | "";
  options: Array<AdminMenuSelectOption<T>>;
  onChange: (value: T) => void;
  placeholder?: string;
  ariaLabel?: string;
  emptyLabel?: string;
  variant?: "field" | "link" | "subtle";
}) {
  const menuId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  useClickOutside(containerRef, close, open, menuRef);
  useAnchoredOverlay({
    open,
    triggerRef,
    overlayRef: menuRef,
    align: variant === "field" ? "stretch" : "end",
    minHeight: 160,
    matchTriggerWidth: variant === "field",
  });

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [close, open]);

  const selected = options.find((item) => item.value === value);

  return (
    <div ref={containerRef} className="relative min-w-0">
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={menuId}
        onClick={() => setOpen((current) => !current)}
        className={
          variant === "field"
            ? "flex h-[2.125rem] w-full items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-left font-onest text-sm text-slate-800 shadow-sm transition-colors hover:border-slate-300 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            : variant === "link"
              ? "flex h-9 max-w-48 items-center gap-1 rounded-md px-1.5 text-left font-onest text-sm font-medium text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              : "flex h-7 max-w-48 items-center gap-1 rounded px-1 text-left font-onest text-xs font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300/60"
        }
      >
        {selected?.icon ? (
          <span
            className={[
              "flex h-5 w-5 shrink-0 items-center justify-center",
              variant === "link" ? "text-blue-500" : "text-slate-500",
            ].join(" ")}
          >
            {selected.icon}
          </span>
        ) : null}
        <span
          className={[
            "min-w-0 flex-1 truncate",
            selected ? "" : "text-slate-400",
          ].join(" ")}
        >
          {selected?.label ?? emptyLabel ?? placeholder}
        </span>
        <IconChevronDown
          size={14}
          className={[
            "shrink-0 transition-transform",
            variant === "link" ? "text-blue-400" : "text-slate-400",
            open ? "rotate-180" : "",
          ].join(" ")}
        />
      </button>

      {open
        ? createPortal(
            <div
              ref={menuRef}
              id={menuId}
              role="listbox"
              data-admin-overlay=""
              aria-label={ariaLabel}
              className={[
                "fixed z-[60] overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg",
                variant === "field" ? "" : "min-w-56",
              ].join(" ")}
              style={{ visibility: "hidden" }}
            >
              {optionGroups(options).map((group, index) => (
                <div
                  key={group.label ?? "__ungrouped"}
                  role={group.label ? "group" : undefined}
                  aria-label={group.label ?? undefined}
                >
                  {group.label ? (
                    <p
                      className={[
                        "px-2.5 pb-0.5 font-onest text-[10px] font-semibold uppercase tracking-wide text-slate-400",
                        index === 0 ? "pt-1.5" : "pt-2",
                      ].join(" ")}
                    >
                      {group.label}
                    </p>
                  ) : null}
                  {group.items.map((option) => {
                    const active = option.value === value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="option"
                        aria-selected={active}
                        aria-disabled={option.disabled || undefined}
                        title={option.disabled ? option.hint : option.label}
                        onClick={() => {
                          if (option.disabled) return;
                          onChange(option.value);
                          close();
                        }}
                        className={[
                          "flex w-full items-center gap-2 px-2.5 py-1.5 text-left font-onest text-sm",
                          option.disabled
                            ? "cursor-not-allowed text-slate-300"
                            : active
                              ? "bg-brand-50 text-brand-800"
                              : "text-slate-700 hover:bg-slate-50",
                        ].join(" ")}
                      >
                        {option.icon ? (
                          <span
                            className={[
                              "flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-[11px] font-semibold",
                              option.disabled
                                ? "bg-slate-50 text-slate-300"
                                : "bg-slate-100 text-slate-600",
                            ].join(" ")}
                          >
                            {option.icon}
                          </span>
                        ) : (
                          <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                            {active ? (
                              <IconCheck size={14} className="text-brand-600" />
                            ) : null}
                          </span>
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{option.label}</span>
                          {option.disabled && option.hint ? (
                            <span className="block truncate text-[10px] leading-tight text-slate-300">
                              {option.hint}
                            </span>
                          ) : option.description ? (
                            <span className="block truncate text-[10px] leading-tight text-slate-400">
                              {option.description}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
